import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import type { SdCycle } from '@hunting-nature/shared-core/enums/remote-configs.enum';
import {
  CameraMode,
  CamNameSetting,
  Command,
  FirmwareUpdate,
  GPS,
  GpsPulse,
  MobileVideo,
  NetworkModuleVersion,
  PinCode,
  UpdateMcu,
  VideoTransmissionType,
} from '@hunting-nature/shared-core/enums/remote-configs.enum';
import type { Camera } from '@hunting-nature/shared-core/interfaces/camera.interface';
import type { Duration } from '@hunting-nature/shared-core/interfaces/remote-control.interface';
import type { UserSubscriptionResponse } from '@hunting-nature/shared-core/interfaces/user-subscription-response.interface';
import type { VideoSettings } from '@hunting-nature/shared-core/interfaces/video-settings.interface';
import type { RemoteControlResponse } from '@hunting-nature/shared-core/types/remote-control-response.type';
import { SubscriptionPlanValues } from '@hunting-nature/shared-core/types/subscription-plan.type';
import { catchError, delay, forkJoin, map, of, type Observable } from 'rxjs';

import { AppConfigLoader } from '../../../config/app-config.loader';
import { formatDelaySettingTime, getCameraIdentCodeByCameraDetails } from '../../../shared/helper/camera.helper';
import { isCameraWithSecaTrack, isLegacyCamera } from '../../../shared/helper/remote-control.helper';
import { AuthenticationService } from '../../../shared/services/authentication.service';
import type {
  RemoteControlJob,
  RemoteControlGpsJob,
  RemoteControlMobileVideoJob,
  RemoteControlCamControlJob,
} from '../camera-details/remote-control/remote-control-configs';

@Injectable({
  providedIn: 'root',
})
export class RemoteControlService {
  // services
  private readonly http: HttpClient = inject(HttpClient);
  private readonly authenticationService = inject(AuthenticationService);
  private readonly appConfigLoader = inject(AppConfigLoader);

  private get backend() {
    return this.appConfigLoader.activeConfig().backend;
  }

  public fetchRemoteControlStatus$(imei: string): Observable<RemoteControlResponse> {
    const url = `${this.backend.remoteControlApiUrl}/camera-state/${imei}/full`;
    return this.http.get<RemoteControlResponse>(url);
  }

  public fetchSubscriptionDetails$(): Observable<UserSubscriptionResponse> {
    const url = `${this.backend.subscriptionApiUrl}/subscription/user/${this.authenticationService.customerId}`;
    return this.http.get<UserSubscriptionResponse>(url).pipe(
      catchError((error: unknown) => {
        // Handle 404 as no subscription found for the user
        if (error && typeof error === 'object' && 'status' in error && error.status === 404) {
          return of({} as UserSubscriptionResponse);
        }
        throw error;
      })
    );
  }

  public applySensitivity$(cameraIdentCode: string, job: RemoteControlJob): Observable<string> {
    return this.executeRemoteControlJob$(Command.SENSITIVITY, cameraIdentCode, job);
  }
  public applyImageSize$(cameraIdentCode: string, job: RemoteControlJob): Observable<string> {
    return this.executeRemoteControlJob$(Command.IMAGE_SIZE, cameraIdentCode, job);
  }
  public applyGps$(cameraIdentCode: string, job: RemoteControlGpsJob): Observable<string> {
    return this.executeRemoteControlJob$(Command.GPS, cameraIdentCode, job);
  }
  public applyGpsPulse$(cameraIdentCode: string, job: RemoteControlJob): Observable<string> {
    return this.executeRemoteControlJob$(Command.GPS_PULSE, cameraIdentCode, job);
  }
  public applyGpsAndGpsPulsForAllCameras(activatedCameras: Array<Camera>): Array<Observable<string>> {
    const requests: Array<Observable<string>> = [];

    activatedCameras.forEach(camera => {
      const subscribed = camera.subscriptionPlan !== SubscriptionPlanValues.NONE;
      const secaTrack = isCameraWithSecaTrack(camera.model);
      const legacy = isLegacyCamera(camera.model);
      const id = getCameraIdentCodeByCameraDetails(camera);

      if (id && !legacy) {
        requests.push(this.applyGps$(id, { gps: GPS.ON }));
      }
      if (id && !legacy && subscribed && secaTrack) {
        requests.push(this.applyGpsPulse$(id, { camControlJob: { gpsPulse: GpsPulse.ON } }));
      }
    });

    return requests;
  }
  public applyImageSelection$(cameraIdentCode: string, job: RemoteControlJob): Observable<string> {
    return this.executeRemoteControlJob$(Command.PIC_NUMBER_TO_SEND, cameraIdentCode, job);
  }
  public applyFooterCamName$(cameraIdentCode: string, job: RemoteControlJob): Observable<string> {
    return this.executeRemoteControlJob$(Command.CAM_NAME, cameraIdentCode, {
      camNameSetting: job.camNameString ? CamNameSetting.ON : CamNameSetting.OFF,
      // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
      camNameString: job.camNameString?.trim().toUpperCase() || 'null',
    });
  }

  public applyDelaySetting$(cameraIdentCode: string, imei: string, activated: boolean, duration?: Duration): Observable<string> {
    const delaySetting = activated ? formatDelaySettingTime(duration!.hours, duration!.minutes, duration!.seconds) : 'OFF';
    const job = { delaySetting: delaySetting } as RemoteControlJob;

    return this.executeRemoteControlJob$(Command.DELAY_SETTING, cameraIdentCode, job);
  }

  public applyTimerSetting$(cameraIdentCode: string, job: RemoteControlJob): Observable<string> {
    return this.executeRemoteControlJob$(Command.TIMER_SETTING, cameraIdentCode, job);
  }

  public applyNightMode$(cameraIdentCode: string, job: RemoteControlJob): Observable<string> {
    return this.executeRemoteControlJob$(Command.NIGHT_MODE, cameraIdentCode, job);
  }

  public applyMaximalSentShotsPerHour$(cameraIdentCode: string, job: RemoteControlJob): Observable<string> {
    return this.executeRemoteControlJob$(Command.MAX_NUM_PER_HOUR, cameraIdentCode, job);
  }

  public applyInstantMode$(cameraIdentCode: string, job: RemoteControlJob): Observable<string> {
    return this.executeRemoteControlJob$(Command.INSTANT_MODE, cameraIdentCode, job);
  }

  public applySnapshotNumber$(cameraIdentCode: string, snapNumber: number, quickPhoto: boolean): Observable<string> {
    return this.executeRemoteControlJob$(Command.SNAP_NUM, cameraIdentCode, { snapNum: (quickPhoto ? Math.max(3, snapNumber) + 3 : snapNumber).toString() });
  }

  public applySendingPicSize$(cameraIdentCode: string, job: RemoteControlJob): Observable<string> {
    return this.executeRemoteControlJob$(Command.SENDING_PIC_SIZE, cameraIdentCode, job);
  }

  public applyVideoSettings$(cameraIdentCode: string, settings: VideoSettings): Observable<string> {
    const job = {
      mobileVideo: settings.videoTransmissionEnabled ? MobileVideo.ON : MobileVideo.OFF,
      cameraMode: settings.videoRecordingEnabled ? CameraMode.ON : CameraMode.OFF,
      videoSize: settings.videoSize,
      videoLength: settings.videoLength?.toString(),
      videoMode: settings.videoTransmissionEnabled ? settings.videoTransmissionType : VideoTransmissionType.NoVideoTransmission,
    } as RemoteControlMobileVideoJob;

    return this.executeRemoteControlJob$(Command.MOBILE_VIDEO, cameraIdentCode, job);
  }

  public applyTimelapseSetting$(cameraIdentCode: string, job: RemoteControlJob): Observable<string> {
    return this.executeRemoteControlJob$(Command.TIMELAPSE, cameraIdentCode, job);
  }

  public applyControlPhotos$(cameraIdentCode: string, controlPic: Date | undefined, controlPic2: Date | undefined): Observable<string> {
    const job: RemoteControlJob = {
      controlPic: {
        controlPic1: controlPic ? this.getTimeString(controlPic) : 'OFF',
        controlPic2: controlPic2 ? this.getTimeString(controlPic2) : 'OFF',
      },
    };

    return this.executeRemoteControlJob$(Command.CONTROL_PIC, cameraIdentCode, job);
  }

  public applySdCycle$(cameraIdentCode: string, selectedSdCycle: SdCycle): Observable<string> {
    const job: RemoteControlJob = {
      cycle: selectedSdCycle,
    };
    return this.executeRemoteControlJob$(Command.SD_CYCLE, cameraIdentCode, job);
  }

  public applyCameraResetFactorySettings$(cameraIdentCode: string): Observable<string> {
    const job: RemoteControlJob = {
      camControlJob: {
        reset: '1',
      },
    };
    return this.executeRemoteControlJob$(Command.RESET, cameraIdentCode, job);
  }

  public sendInstantPic$(cameraIdentCode: string): Observable<string> {
    const job: RemoteControlJob = {
      camControlJob: {
        sendInstantPic: '1',
      },
    };
    return this.executeRemoteControlJob$(Command.SEND_INSTANT_PIC, cameraIdentCode, job);
  }

  public applyPinCode$(cameraIdentCode: string): Observable<string> {
    const job: RemoteControlJob = {
      camControlJob: {
        pinCode: PinCode.OFF,
      },
    };
    return this.executeRemoteControlJob$(Command.PIN_CODE, cameraIdentCode, job);
  }

  public updateFirmware$(cameraIdentCode: string): Observable<string> {
    const job: RemoteControlJob = {
      camControlJob: {
        updateFirmware: FirmwareUpdate.DownloadOnly,
        networkModuleVersion: NetworkModuleVersion.Default,
        updateMcu: UpdateMcu.Default,
      },
    };
    return this.executeRemoteControlJob$(Command.UPDATE_FIRMWARE, cameraIdentCode, job);
  }

  public applyStatusReport$(cameraIdentCode: string, interval: string): Observable<string> {
    const job: RemoteControlJob = {
      camControlJob: {
        sendReportInterval: interval,
      },
    };
    return this.executeRemoteControlJob$(Command.SEND_REPORT_INTERVAL, cameraIdentCode, job);
  }

  private executeRemoteControlJob$(command: Command, cameraIdentCode: string, job: RemoteControlJob | RemoteControlMobileVideoJob): Observable<string> {
    // cameraIdentCode = IMEI (camera.products.cameras.imei) + SIM Card Number (camera.products.simCards.simCardNr)
    const url = `${this.backend.remoteControlUpdateApiUrl}/rcjobs/cameraidentcodes/${cameraIdentCode}`;
    return this.http.post(url, { command, job }, { responseType: 'text' }).pipe(delay(2000));
  }

  isGPSActive$(imei: string): Observable<boolean> {
    return this.fetchRemoteControlStatus$(imei).pipe(
      map((response: RemoteControlResponse) => {
        const pendingStateAvailable = Object.keys(response?.states?.pendingState)?.length > 0;
        const state = pendingStateAvailable ? response.states.pendingState : response.states.acknowledgedState;
        return state.gps === GPS.ON;
      })
    );
  }

  reloadGps$(cameraIdentCode: string): Observable<string> {
    return this.executeRemoteControlJob$(Command.SEND_GPS, cameraIdentCode, {
      camControlJob: { sendGPS: GPS.ON },
    } as RemoteControlCamControlJob);
  }

  private getTimeString(date: Date): string {
    return date.toLocaleTimeString(undefined, {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });
  }
}
